import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'

import { WizardModal } from '@/components/layout/modals/WizardModal'
import { closeModal } from '@/reducers/ui/ui.redux'
import {
  createContactList,
  updateContactList,
  uploadContacts,
  syncContacts,
} from '@/reducers/outbound/outboundContactLists.actions'
import { ContactListDetailsPage } from './ContactListDetailsPage'
import { ContactListUploadPage } from './ContactListUploadPage'

export const ContactListWizard = ({ contactList }) => {
  const dispatch = useDispatch()
  const isEditing = !!contactList
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  const initialValues = useMemo(
    () => ({
      name: contactList?.name || '',
      description: contactList?.description || '',
      dnc_scrub_acknowledged: contactList?.dnc_scrub_acknowledged || false,
      file: [],
      contactListId: contactList?.id || null,
    }),
    [contactList]
  )

  const pages = [
    () => <ContactListDetailsPage />,
    (values, _goToPageNumber, _errors, setFieldValue, formikProps) => (
      <ContactListUploadPage
        values={values}
        setFieldValue={setFieldValue}
        formikProps={formikProps}
        uploadError={uploadError}
      />
    ),
  ]

  const handlePageChange = async (values, nextPageIndex, setFieldValue) => {
    if (nextPageIndex === 1) {
      const trimmedName = values.name.trim()
      const trimmedDescription = values.description.trim()
      const nameChanged = trimmedName !== contactList?.name
      const descriptionChanged = trimmedDescription !== (contactList?.description || '')
      const dncScrubChanged = values.dnc_scrub_acknowledged !== contactList?.dnc_scrub_acknowledged

      if (isEditing && (nameChanged || descriptionChanged || dncScrubChanged)) {
        await dispatch(
          updateContactList(contactList.id, {
            name: trimmedName,
            description: trimmedDescription,
            dnc_scrub_acknowledged: values.dnc_scrub_acknowledged,
          })
        )
      } else if (!values.contactListId) {
        const response = await dispatch(
          createContactList({
            name: trimmedName,
            description: trimmedDescription,
            dnc_scrub_acknowledged: values.dnc_scrub_acknowledged,
          })
        )
        if (response) {
          setFieldValue('contactListId', response.id)
        }
      }
    }
  }

  const handleSave = async (values) => {
    setUploadError(null)

    if (!isEmpty(values.file) && values.contactListId) {
      setUploading(true)
      const upload = isEditing ? syncContacts : uploadContacts
      const result = await dispatch(upload(values.contactListId, values.file[0]))
      setUploading(false)

      if (!result.success) {
        setUploadError(result.error)
        return
      }
    }

    dispatch(closeModal())
  }

  const validate = (values, currentPage) => {
    const errors = {}
    if (currentPage === 0) {
      if (!values.name?.trim()) {
        errors.name = 'Name is required'
      }
      if (!values.dnc_scrub_acknowledged) {
        errors.dnc_scrub_acknowledged = 'DNC scrub acknowledgment is required'
      }
    }
    return errors
  }

  return (
    <WizardModal
      title={isEditing ? 'Edit Contact List' : 'Create Contact List'}
      pages={pages}
      initialValues={initialValues}
      initialTouched={{ name: true }}
      onSave={handleSave}
      onPageChange={handlePageChange}
      validate={validate}
      rightButtonSubmitLabel="Save"
      size="small"
      isLoading={uploading}
      alwaysEnablePreviousButton
      canCloseOnDimmerClick={false}
    />
  )
}
